import React, { useState, useEffect } from 'react';
import { Check, X } from '../../icons';
import InfoBanner from '../common/InfoBanner';
import Button from '../ui/Button';
import Toggle from '../ui/Toggle';
import Chip, { ChipRow } from '../ui/Chip';
import { CardSection } from '../ui/Card';

const BANNER_TYPES = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
  { value: 'success', label: 'Success' }
];

const InfoBannerSettings = ({ settings, onSave, userId, isLoading }) => {
  const [formData, setFormData] = useState({
    visible: true,
    title: '',
    message: '',
    type: 'warning'
  });
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Initialize form with current settings
  useEffect(() => {
    if (settings) {
      setFormData({
        visible: settings.visible ?? true,
        title: settings.title ?? '',
        message: settings.message ?? '',
        type: settings.type ?? 'warning'
      });
      setHasChanges(false);
    }
  }, [settings]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSaveMessage(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      await onSave(formData, userId);
      setHasChanges(false);
      setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      console.error('Error saving banner settings:', error);
      setSaveMessage({ type: 'error', text: `Failed to save: ${error.message}` });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setFormData({
        visible: settings.visible ?? true,
        title: settings.title ?? '',
        message: settings.message ?? '',
        type: settings.type ?? 'warning'
      });
      setHasChanges(false);
      setSaveMessage(null);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-card border border-line rounded-card p-4 animate-pulse space-y-3" aria-busy="true">
        <div className="h-5 bg-card-2 rounded w-1/3" />
        <div className="h-11 bg-card-2 rounded-xl" />
        <div className="h-24 bg-card-2 rounded-xl" />
      </div>
    );
  }

  return (
    <CardSection
      title="Info banner"
      action={
        <div className="flex items-center gap-2 text-sm text-secondary">
          <span>{formData.visible ? 'Visible' : 'Hidden'}</span>
          <Toggle checked={formData.visible} onChange={(v) => handleChange('visible', v)} label="Banner visible" size="sm" />
        </div>
      }
      className="space-y-4"
    >
      {/* Banner Type */}
      <div>
        <span className="label">Banner type</span>
        <ChipRow>
          {BANNER_TYPES.map(type => (
            <Chip key={type.value} active={formData.type === type.value} onClick={() => handleChange('type', type.value)}>
              {type.label}
            </Chip>
          ))}
        </ChipRow>
      </div>

      {/* Title */}
      <div>
        <label className="label" htmlFor="banner-title">Title</label>
        <input
          id="banner-title"
          type="text"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="Enter banner title…"
          className="input"
        />
      </div>

      {/* Message */}
      <div>
        <label className="label" htmlFor="banner-message">Message</label>
        <textarea
          id="banner-message"
          value={formData.message}
          onChange={(e) => handleChange('message', e.target.value)}
          placeholder="Enter banner message…"
          rows={4}
          className="input resize-none"
        />
      </div>

      {/* Live Preview */}
      <div>
        <span className="label">Preview</span>
        {formData.visible ? (
          <InfoBanner settings={formData} />
        ) : (
          <p className="text-secondary text-sm italic">Banner is currently hidden. Toggle visibility above to show it.</p>
        )}
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div
          role="status"
          className={`p-3 rounded-xl text-sm border ${
            saveMessage.type === 'success' ? 'bg-accent-soft text-accent border-accent/30' : 'bg-danger-soft text-danger border-danger/30'
          }`}
        >
          {saveMessage.text}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button variant="primary" onClick={handleSave} disabled={!hasChanges || isSaving} loading={isSaving}>
          {!isSaving && <Check className="w-4 h-4" />}
          {isSaving ? 'Saving…' : 'Save changes'}
        </Button>
        <Button variant="ghost" onClick={handleReset} disabled={!hasChanges || isSaving}>
          <X className="w-4 h-4" /> Reset
        </Button>
      </div>
    </CardSection>
  );
};

export default InfoBannerSettings;
